import * as Progress from '@radix-ui/react-progress';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { colors } from '../theme/colors.stylex';
import type { ProgressBarProps, ProgressIndicatorProps } from './ProgressBar.types';

const PROGRESS_BAR_ANIMATION_CONFIG: KeyframeAnimationOptions = {
  duration: 660,
  easing: 'ease-in-out',
  fill: 'forwards',
};

const styles = stylex.create({
  root: {
    position: 'relative',
    display: 'flex',
    height: '1.125rem',
    width: '100%',
    flex: '1 1 0%',
    overflow: 'hidden',
    borderRadius: '0.9375rem',
    backgroundColor: colors.trackBackground,
  },
  indicator: {
    display: 'flex',
    height: '1.125rem',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.accent,
  },
  overflow: { overflow: 'hidden' },
  roundedLeft: { borderTopLeftRadius: '0.9375rem', borderBottomLeftRadius: '0.9375rem' },
  roundedRight: { borderTopRightRadius: '0.9375rem', borderBottomRightRadius: '0.9375rem' },
  label: {
    left: '0.25rem',
    fontSize: '0.75rem',
    lineHeight: '1rem',
    color: colors.text,
    alignItems: 'center',
    height: '1.125rem',
  },
  onDark: { color: colors.white },
  onLight: { color: colors.textOnLight },
  singleLabel: { position: 'absolute', zIndex: 50 },
  emptyLabel: { marginLeft: 'auto', marginRight: 'auto', overflow: 'hidden' },
});

const ProgressIndicator = (
  props: ProgressIndicatorProps & { max: number; valuesLength: number; index: number; totalValue: number },
) => {
  const {
    value,
    color,
    testId,
    label,
    extraClasses = '',
    labelClasses = '',
    xstyle,
    labelXstyle,
    valuesLength,
    max,
    index,
    totalValue,
    ...tooltipProps
  } = props;
  const tooltipData = getQTipData(tooltipProps);

  const previousWidth = useRef(0);
  const animationRef = useRef<Animation | null>(null);
  const indicatorElementRef = useRef<HTMLDivElement | null>(null);
  const [isDarkBackground, setIsDarkBackground] = useState(false);

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const indicatorTestId = testId ? `progress-bar-indicator-${testId}` : 'progress-bar-indicator';
  const newWidth = (Number(value) / Number(max)) * 100;

  useEffect(() => {
    if (!indicatorElementRef.current) {
      return;
    }

    function cleanupAnimation() {
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
    }

    const oldWidth = previousWidth.current;

    cleanupAnimation();

    indicatorElementRef.current.style.width = `${newWidth}%`;

    if (!prefersReducedMotion && newWidth !== oldWidth) {
      animationRef.current = indicatorElementRef.current.animate(
        [{ width: `${oldWidth}%` }, { width: `${newWidth}%` }],
        PROGRESS_BAR_ANIMATION_CONFIG,
      );
    }

    previousWidth.current = newWidth;

    return () => cleanupAnimation();
  }, [newWidth, prefersReducedMotion]);

  useEffect(() => {
    if (indicatorElementRef.current) {
      setIsDarkBackground(tinycolor(getComputedStyle(indicatorElementRef.current).backgroundColor).isDark());
    }
  }, [color]);

  const indicatorProps = stylex.props(
    styles.indicator,
    valuesLength !== 1 && styles.overflow,
    index === 0 && styles.roundedLeft,
    totalValue >= max && index === valuesLength - 1 && styles.roundedRight,
    xstyle,
  );
  const labelProps = stylex.props(
    styles.label,
    isDarkBackground ? styles.onDark : styles.onLight,
    valuesLength === 1 && styles.singleLabel,
    labelXstyle,
  );

  return (
    <Progress.Indicator
      {...indicatorProps}
      className={clsx(indicatorProps.className, extraClasses)}
      {...tooltipData}
      data-qtip-text={tooltipProps.tooltip ? tooltipProps.tooltip : `${value}%`}
      data-segment-value={value}
      data-testid={indicatorTestId}
      ref={indicatorElementRef}
      style={{
        // Background color will default to the theme color if undefined
        ...indicatorProps.style,
        backgroundColor: color || undefined,
        width: prefersReducedMotion ? `${newWidth}%` : undefined,
      }}>
      {label ? (
        <span {...labelProps} className={clsx(labelProps.className, labelClasses)}>
          {label}
        </span>
      ) : undefined}
    </Progress.Indicator>
  );
};

const ProgressBar = ({
  values,
  max = 100,
  containerExtraClasses = '',
  xstyle,
  zeroValueLabel = 'No progress yet',
}: ProgressBarProps) => {
  const rootProps = stylex.props(styles.root, xstyle);
  const totalValue = values.reduce((acc, { value }) => acc + value, 0);
  return (
    <Progress.Root
      {...rootProps}
      className={clsx(rootProps.className, containerExtraClasses)}
      max={max}
      value={totalValue}>
      {totalValue > 0 ? (
        values.map((props, i) => {
          return (
            <ProgressIndicator
              {...props}
              max={max}
              valuesLength={values.length}
              index={i}
              totalValue={totalValue}
              key={i}
            />
          );
        })
      ) : (
        <div {...stylex.props(styles.label, styles.emptyLabel)}>{zeroValueLabel}</div>
      )}
    </Progress.Root>
  );
};

export default ProgressBar;
